import { Input, message, Tabs, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { Button } from '@/components/ui/button';
import { brandStore } from '@/stores';
import { appConfigAdminStore } from '@/stores/appConfigAdminStore';
import { authStore } from '@/stores/authStore';
import type { DesktopUpdateApproval, DesktopUpdateProduct } from '@/types';
import { isNextopBrand } from '@/utils/brand';
import { DesktopFeatureAllowlistEditor } from './DesktopFeatureAllowlistEditor';
import {
  DesktopUpdateApprovalList,
  formatDesktopUpdateDateTime,
} from './DesktopUpdateApprovalList';
import {
  activeDesktopUpdateApproval,
  buildDesktopUpdateChangePreview,
  cloneDesktopUpdateProduct,
  DESKTOP_UPDATE_CHANNELS,
  DESKTOP_UPDATE_PRODUCTS,
  DESKTOP_UPDATE_TARGETS,
  desktopUpdateUserLabel,
  effectiveDesktopMinimumVersion,
  isCatalogDesktopUpdateProduct,
  isDesktopUpdateReleaseAvailable,
  validateDesktopFeatureDraft,
} from './desktopUpdatePolicyModel';

function desktopUpdatePublicationView(approval: DesktopUpdateApproval) {
  switch (approval.publication?.status) {
    case 'deploying':
      return {
        color: 'blue',
        label: '发布中',
        headline: '发布中：当前卡片展示正在发布的目标配置',
      };
    case 'failed':
      return {
        color: 'red',
        label: '发布失败',
        headline: '发布失败：系统将自动重试当前目标配置',
      };
    case 'superseded':
      return {
        color: 'default',
        label: '发布已替代',
        headline: '发布已终止：当前任务已被后续配置替代',
      };
    case 'published':
      return {
        color: 'green',
        label: '发布已完成',
        headline: '发布已完成：等待审批状态同步',
      };
    default:
      return {
        color: 'gold',
        label: '等待发布',
        headline: '等待发布：当前卡片展示已审批的目标配置',
      };
  }
}

function DesktopUpdatesPageContent() {
  const available = isNextopBrand(brandStore.activeBrand);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.desktopUpdatesWrite);
  const products = DESKTOP_UPDATE_PRODUCTS;
  const view = appConfigAdminStore.getDesktopDocument();
  const [drafts, setDrafts] = useState<Record<string, DesktopUpdateProduct>>(
    {},
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<
    'minimum' | 'features' | 'approvals'
  >('minimum');
  const [activeFeatureProduct, setActiveFeatureProduct] = useState<string>(
    products[0],
  );
  const draftSourceRevision = useRef('');

  useEffect(() => {
    setActiveFeatureProduct(products[0]);
  }, [products]);

  useEffect(() => {
    if (available) {
      void appConfigAdminStore
        .fetchDesktopUpdatesDocument()
        .catch(() => undefined);
    }
  }, [available]);

  useEffect(() => {
    if (!view?.document.products) {
      return;
    }
    const activeApprovals = products.map((product) =>
      activeDesktopUpdateApproval(view.approvals, product),
    );
    const sourceRevision = [
      view.currentVersion?.versionNumber || 0,
      ...activeApprovals.map((approval) =>
        approval
          ? `${approval.id}:${approval.status}:${approval.targetHash}`
          : 'none',
      ),
    ].join('|');
    if (draftSourceRevision.current === sourceRevision) {
      return;
    }
    draftSourceRevision.current = sourceRevision;
    setDrafts(
      Object.fromEntries(
        Object.entries(view.document.products)
          .filter(([key]) => isCatalogDesktopUpdateProduct(key))
          .map(([key, value]) => [
            key,
            cloneDesktopUpdateProduct(
              activeDesktopUpdateApproval(view.approvals, key)?.targetProduct ||
                value,
            ),
          ]),
      ),
    );
  }, [products, view]);

  const catalogApprovals = useMemo(
    () =>
      (view?.approvals || []).filter((approval) =>
        isCatalogDesktopUpdateProduct(approval.product),
      ),
    [view?.approvals],
  );

  const publicationActive = useMemo(
    () =>
      catalogApprovals.some(
        (approval) =>
          approval.status === 'approved' ||
          approval.publication?.status === 'pending' ||
          approval.publication?.status === 'deploying' ||
          approval.publication?.status === 'failed',
      ),
    [catalogApprovals],
  );

  useEffect(() => {
    if (!available || !publicationActive) {
      return;
    }
    const timer = window.setInterval(() => {
      if (!appConfigAdminStore.isDesktopLoading()) {
        void appConfigAdminStore
          .fetchDesktopUpdatesDocument({ preserveApprovalHistory: true })
          .catch(() => undefined);
      }
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [available, publicationActive]);

  const pendingCount = useMemo(
    () => catalogApprovals.filter((item) => item.status === 'pending').length,
    [catalogApprovals],
  );

  const updateDraft = (
    product: string,
    apply: (draft: DesktopUpdateProduct) => void,
  ) => {
    setDrafts((current) => {
      const next = cloneDesktopUpdateProduct(current[product]);
      apply(next);
      return { ...current, [product]: next };
    });
  };

  const submitProduct = async (product: string) => {
    const validationError = validateDesktopFeatureDraft(drafts[product]);
    if (validationError) {
      message.error(validationError);
      return;
    }
    const reason = reasons[product]?.trim();
    if (!reason) {
      message.error('必须填写变更原因');
      return;
    }
    try {
      const result = await appConfigAdminStore.changeDesktopUpdateProduct({
        product,
        targetProduct: drafts[product],
        reason,
      });
      message.success(
        result.approval?.status === 'published'
          ? `已发布 AppConfig v${result.approval.finalAppConfigVersion}`
          : result.approval?.status === 'pending'
            ? '已提交审批'
            : '发布任务已受理',
      );
      setReasons((current) => ({ ...current, [product]: '' }));
    } catch (error) {
      message.error(error instanceof Error ? error.message : '提交失败');
    }
  };

  if (!available) {
    return (
      <div className="rounded-lg border border-border bg-background p-6 text-sm">
        当前能力仅对 nextop Brand 开放。
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="rounded-lg border border-border bg-background p-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold">桌面端更新策略</h1>
          {pendingCount > 0 ? (
            <Tag color="gold">待审批 {pendingCount}</Tag>
          ) : null}
          <Tag>AppConfig v{view?.currentVersion?.versionNumber || '—'}</Tag>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          最低版本收紧与 Feature
          白名单扩张需要不同固定审批人通过；放宽或收缩填写原因后直接发布。
        </p>
      </header>

      <div className="space-y-2">
        <Tabs
          activeKey={activeTab}
          items={[
            { key: 'minimum', label: '最低版本' },
            { key: 'features', label: 'Feature 白名单' },
            {
              key: 'approval-divider',
              disabled: true,
              label: (
                <span
                  aria-hidden="true"
                  className="block h-5 border-l border-border"
                />
              ),
            },
            {
              key: 'approvals',
              label: (
                <span className="inline-flex items-center gap-2 font-medium">
                  审批记录
                  {pendingCount ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">
                      待处理 {pendingCount}
                    </span>
                  ) : null}
                </span>
              ),
            },
          ]}
          onChange={(key) =>
            setActiveTab(key as 'minimum' | 'features' | 'approvals')
          }
        />

        {activeTab === 'features' ? (
          <Tabs
            size="small"
            activeKey={activeFeatureProduct}
            items={products.map((product) => ({
              key: product,
              label: product,
            }))}
            onChange={(key) => setActiveFeatureProduct(key)}
          />
        ) : null}
      </div>

      {products.map((product) => {
        if (activeTab === 'approvals') {
          return null;
        }
        if (activeTab === 'features' && product !== activeFeatureProduct) {
          return null;
        }
        const draft = drafts[product];
        if (!draft) {
          return null;
        }
        const activeApproval = activeDesktopUpdateApproval(
          view?.approvals || [],
          product,
        );
        const eligibleApprovers =
          view?.approverUids
            .filter((userID) => userID !== activeApproval?.submitterUid)
            .map((userID) =>
              desktopUpdateUserLabel(view.userIdentities, userID),
            ) || [];
        const cardReadOnly = !canWrite || Boolean(activeApproval);
        const publication = activeApproval?.publication;
        const publicationView =
          activeApproval?.status === 'approved'
            ? desktopUpdatePublicationView(activeApproval)
            : undefined;
        const current = view?.document.products[product];
        const preview = current
          ? buildDesktopUpdateChangePreview(current, draft)
          : { differences: [], affectedTargets: [] };
        return (
          <section
            key={product}
            className="space-y-5 rounded-lg border border-border bg-background p-6 shadow-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold">{product}</h2>
                  {activeApproval?.status === 'pending' ? (
                    <Tag color="gold">审批中 #{activeApproval.id}</Tag>
                  ) : null}
                  {activeApproval?.status === 'approved' ? (
                    <Tag color={publicationView?.color}>
                      {publicationView?.label} #{activeApproval.id}
                    </Tag>
                  ) : null}
                </div>
                <div className="mt-1 break-all font-mono text-xs text-muted-foreground">
                  {draft.releaseBaseUrl}
                </div>
              </div>
            </div>

            {activeApproval ? (
              <div className="rounded-md border border-border bg-muted/30 p-4 text-sm">
                <div className="font-medium">
                  {activeApproval.status === 'pending'
                    ? '审批中：当前卡片展示待审批的目标配置'
                    : publicationView?.headline}
                </div>
                <div className="mt-1 text-xs">
                  线上仍为 AppConfig v
                  {view?.currentVersion?.versionNumber || '—'}；审批单 #
                  {activeApproval.id} 由{' '}
                  {desktopUpdateUserLabel(
                    view?.userIdentities || {},
                    activeApproval.submitterUid,
                  )}{' '}
                  提交。
                </div>
                {activeApproval.status === 'pending' ? (
                  <div className="mt-1 text-xs">
                    可审批人：
                    {eligibleApprovers.length
                      ? eligibleApprovers.join('、')
                      : '没有符合“非提交人”要求的固定审批人'}
                  </div>
                ) : null}
                {publication?.status === 'failed' ? (
                  <div className="mt-2 rounded-md bg-destructive/10 p-3 text-xs text-destructive">
                    <div>
                      {publication.lastError || '发布失败，错误信息不可用'}
                    </div>
                    <div className="mt-1">
                      已尝试 {publication.attemptCount} 次；下次重试：
                      {formatDesktopUpdateDateTime(publication.nextAttemptAt)}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {activeTab === 'minimum' ? (
              <div className="grid gap-5 xl:grid-cols-2">
                {DESKTOP_UPDATE_CHANNELS.map((channel) => (
                  <div
                    key={channel}
                    className="space-y-4 rounded-md border border-border p-4"
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium uppercase">{channel}</h3>
                      <span className="text-xs text-muted-foreground">
                        latest pointer：
                        {view?.latestVersions[product]?.[channel] || '不可用'}
                      </span>
                    </div>
                    <label
                      className="block space-y-1 text-sm"
                      htmlFor={`${product}-${channel}-default-minimum`}
                    >
                      <span>统一最低版本</span>
                      <Input
                        id={`${product}-${channel}-default-minimum`}
                        disabled={cardReadOnly}
                        value={draft.channels[channel].defaultMinimumVersion}
                        placeholder={
                          channel === 'stable'
                            ? '留空表示不限制，例如 1.6.0'
                            : '留空表示不限制，例如 1.7.0-rc.2'
                        }
                        onChange={(event) =>
                          updateDraft(product, (value) => {
                            value.channels[channel].defaultMinimumVersion =
                              event.target.value;
                          })
                        }
                      />
                    </label>
                    {DESKTOP_UPDATE_TARGETS.map((target) => {
                      const releaseAvailable = isDesktopUpdateReleaseAvailable(
                        draft,
                        target.platform,
                        target.architecture,
                      );
                      return (
                        <div
                          key={target.key}
                          className="grid grid-cols-[1fr_1.4fr] gap-3"
                        >
                          <div className="text-sm">
                            <div>{target.platform}</div>
                            <div className="text-xs text-muted-foreground">
                              {target.architecture} ·{' '}
                              {releaseAvailable
                                ? '发布产物可用'
                                : '暂无发布产物'}
                            </div>
                          </div>
                          <div>
                            <Input
                              aria-label={`${product} ${channel} ${target.key} 最低版本`}
                              disabled={cardReadOnly}
                              value={
                                draft.channels[channel].targetOverrides[
                                  target.key
                                ] || ''
                              }
                              placeholder="继承统一版本"
                              onChange={(event) =>
                                updateDraft(product, (value) => {
                                  const overrides =
                                    value.channels[channel].targetOverrides;
                                  if (event.target.value) {
                                    overrides[target.key] = event.target.value;
                                  } else {
                                    delete overrides[target.key];
                                  }
                                })
                              }
                            />
                            <div className="mt-1 text-xs text-muted-foreground">
                              策略生效：
                              {effectiveDesktopMinimumVersion(
                                draft,
                                channel,
                                target.platform,
                                target.architecture,
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              <DesktopFeatureAllowlistEditor
                current={current}
                draft={draft}
                product={product}
                readOnly={cardReadOnly}
                update={(apply) => updateDraft(product, apply)}
              />
            )}

            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Input.TextArea
                disabled={cardReadOnly}
                rows={2}
                value={reasons[product] || ''}
                onChange={(event) =>
                  setReasons((current) => ({
                    ...current,
                    [product]: event.target.value,
                  }))
                }
                placeholder="必填：说明变更原因、影响目标与回滚预期"
              />
              <Button
                disabled={
                  !canWrite ||
                  Boolean(activeApproval) ||
                  appConfigAdminStore.isDesktopSaving() ||
                  preview.differences.length === 0
                }
                onClick={() => void submitProduct(product)}
              >
                提交变更
              </Button>
            </div>
          </section>
        );
      })}

      {activeTab === 'approvals' ? (
        <DesktopUpdateApprovalList
          approvals={catalogApprovals}
          canWrite={canWrite}
          canApprove={view?.currentUserCanApprove || false}
          currentUserId={authStore.user?.user_id}
          approverUids={view?.approverUids || []}
          userIdentities={view?.userIdentities || {}}
          hasMore={Boolean(view?.nextApprovalPageToken)}
        />
      ) : null}
    </div>
  );
}

export const DesktopUpdatesPage = observer(DesktopUpdatesPageContent);
